import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { StreamStatus, UploadFileInfo } from '../common/api.types';

/**
 * Contract for the multi-omics pipeline (methylation / RNA-seq / proteomics).
 *
 * Deliberately separate from AppService. The two pipelines share almost
 * nothing: different endpoint tree (`/aha/v1/omics/…`), and even their own
 * session stores — tabular posts to `/sessions`, omics to `/omics/sessions`.
 * Keeping them in one class would put ~20 more abstract methods next to
 * tabular's 57 and grow both demo and real services past a thousand lines
 * describing two systems that never call each other.
 *
 * The generic DTOs (StreamStatus, UploadFileInfo) are imported rather than
 * redeclared — they describe HTTP shapes both pipelines happen to use, not
 * tabular behaviour.
 */

/** What the omics choose-module collects, in the shape POST /omics/sessions
 *  wants it. `modality` doubles as the URL segment of every stage endpoint. */
export interface OmicsRunConfig {
  modality: OmicsModality;
  data_dir: string;
  csv_paths: string[];
}

export type OmicsModality = 'meth' | 'rnaseq' | 'proteomics';

/** A row of the omics run history — the shape GET /omics/sessions returns.
 *
 *  Not the tabular Session: the stage vocabulary is different (four stages,
 *  not per-module steps) and so is `status`. Note `pending` after job 1 means
 *  "waiting on you" at the review gate, not "not started" — the docs call that
 *  out because `running` would be a lie the UI cannot recover from. */
export interface OmicsSession {
  session_id: string;
  name: string;
  modality: OmicsModality;
  /** Same four values as the tabular Session, and read the same way: `canceled` is a
   *  user-stopped run, stored as failed with a marker so the label stays accurate. A run
   *  parked at the review gate is `running` — where it is parked is `current_stage`, the
   *  same division of labour the tabular side makes between `status` and `current_step`. */
  status: 'running' | 'finished' | 'failed' | 'canceled';
  /** Mirrors the tabular Session's `current_step`: the stage, plus `_sse` while it is
   *  running and `_failed` when it stopped there. A string for the same reason — the
   *  suffixed combinations are a map lookup, not a union worth spelling out. */
  current_stage: string;
  config?: Record<string, unknown>;
  /** Who created the run; empty when the backend has no user attached. */
  user_email?: string;
  created_at: string;
  updated_at: string;
}

export type OmicsStage = 'ingestion' | 'strategy' | 'processing' | 'analysis';

/** One row of the harmonization plan: how a raw clinical column was read.
 *  `source_column === target_column` means it kept its name. */
export interface ColumnMapping {
  source_column: string;
  target_column: string;
  value_replacements: Record<string, string>;
}

/** GET .../ingestion/summary — the whole page in one response.
 *
 *  Three sections come out of this: `counts` + `modality` + `array_type` feed
 *  the stat tiles, `harmonization_plan` feeds the table, `warnings` the list.
 *  Deliberately one call: the alternative is four, two of which would have to
 *  ship a 103 KB sample sheet and a 327 KB metadata file to the browser just
 *  to count their rows. `chart_entries` is always empty here — no chart stage
 *  maps to ingestion. */
export interface IngestionSummary {
  modality: OmicsModality;
  array_type: string;
  counts: {
    raw_files: number;
    samples: number;
    metadata_columns: number;
    mappings: number;
    recoded: number;
  };
  harmonization_plan: {
    schema_fingerprint: string;
    column_mappings: ColumnMapping[];
    idat_construction?: { columns_to_concatenate: string[]; separator: string };
  };
  warnings: { kind: string; detail: string }[];
  chart_entries: unknown[];
}

/** One column of the harmonized metadata, as the curation gate profiles it.
 *  `unique`/`missing` are what tell a reviewer whether a column can be an
 *  outcome at all, and they decide what `chart: 'auto'` resolves to. */
export interface OmicsMetaColumn {
  name: string;
  type: 'text' | 'numeric';
  unique: number;
  missing: number;
}

/** A candidate the gate recommends, with the reason it matched. The backend
 *  ranks these itself (fuzzy match against a canonical vocabulary), so the
 *  frontend never carries a copy of that vocabulary — it filters the ranking
 *  when a class is dropped and takes the best survivor. */
export interface OmicsSuggestion {
  value: string;
  score: number;
  reason: string;
}

/** One value of the outcome column, with how many samples carry it. */
export interface OmicsClass {
  value: string;
  n: number;
}

/** A metadata column offered as a covariate. Columns on the causal denylist
 *  (braak, CERAD, MMSE, cogdx…) and ones screened out on usability never reach
 *  this list, so everything here is selectable. */
export interface OmicsCovariateOption {
  column: string;
  suggested: boolean;
  reason: string;
}

/** One EDA panel. `chart: 'auto'` is re-resolved against the real series at
 *  render time, so it is kept as-is until the user picks a kind explicitly.
 *  `value_labels` is display-only — the analysis still sees the raw values. */
export interface OmicsEdaVariable {
  column: string;
  plot: boolean;
  chart: 'auto' | 'bar' | 'histogram';
  label: string;
  value_labels: Record<string, string>;
  /** Observed values, capped by the backend. */
  values: string[];
  /** How many distinct values exist when `values` is a truncated view. */
  total_values?: number;
}

/** The envelope the pipeline writes to `01_eda/charts/*.json`, verbatim.
 *
 *  `data` and `options` are Chart.js config as-is — the backend already decided the axis
 *  titles and the chart kind, so they are handed to Chart.js untouched rather than being
 *  re-derived here. `chart_id` is keyed on the column, so it survives a relabel and the
 *  frontend's references stay valid across a rebuild. */
export interface OmicsChart {
  schema_version?: number;
  chart_id: string;
  stage?: string;
  stage_title?: string;
  title: string;
  subtitle: string;
  contrast?: string | null;
  type: string;
  data: {
    labels: string[];
    datasets: { label: string; data: number[]; [k: string]: unknown }[];
  };
  options?: Record<string, unknown>;
}

/** Something the EDA noticed. `verdict` is present on the design-confounding
 *  rows and absent on the free-standing warnings. */
export interface OmicsFinding {
  title: string;
  detail: string;
  verdict?: 'ok' | 'moderate' | 'severe' | 'undetermined';
}

/** One line of the proposed plan. Fixed shape so the page renders whatever the
 *  Strategist decided to send, without a branch per parameter. `note` is
 *  optional: a row with nothing to justify simply omits it. */
export interface OmicsPlanRow {
  label: string;
  value: string;
  note?: string;
}

/** The description of one editable parameter, straight from the pipeline's
 *  param_spec registry. The set differs per modality — methylation has 33,
 *  RNA-seq 8, and they are not the same 8 — so the form is generated from this
 *  rather than written out. */
export interface OmicsParamSpec {
  name: string;
  label: string;
  type: 'float' | 'int' | 'str' | 'bool';
  value: string | number | boolean;
  help?: string;
  min?: number;
  max?: number;
  choices?: string[];
  /** Free-text grouping for the form; rows with the same one render together. */
  group: string;
}

/** What the page sends back when the reviewer confirms. Mirrors the gate's own
 *  split: cohort parameters, the EDA panels, and the strategy parameters. */
export interface StrategyConfirm {
  target_column: string;
  covariates: string[];
  reference_class: string | null;
  exclude_classes: string[];
  eda_variables: OmicsEdaVariable[];
  params: Record<string, string | number | boolean>;
}

/** GET .../strategy/review — the whole gate in one response.
 *
 *  One call rather than five: the four cards are one decision, and the plan
 *  cannot be shown before the cohort is confirmed anyway. Same reasoning as
 *  `IngestionSummary`. */
export interface StrategyReview {
  outcome: {
    column: string;
    columns: OmicsMetaColumn[];
    suggested: OmicsSuggestion[];
  };
  covariates: {
    selected: string[];
    options: OmicsCovariateOption[];
  };
  classes: OmicsClass[];
  /** Ranked control-group candidates. Empty means nothing to auto-detect. */
  reference_suggested: OmicsSuggestion[];
  eda_variables: OmicsEdaVariable[];
  charts: OmicsChart[];
  findings: OmicsFinding[];
  plan: OmicsPlanRow[];
  param_spec: OmicsParamSpec[];
}

/** One number the run has to be judged by, as it goes on a stat card. */
export interface OmicsProcessingStat {
  key: string;
  label: string;
  value: string;
  detail?: string;
}

/** The processed matrix, its numbers and whatever the validator wants flagged. */
export interface ProcessingSummary {
  file: string;
  probes: number;
  samples: number;
  stats: OmicsProcessingStat[];
  findings: OmicsFinding[];
}

/** A corner of the matrix: enough rows and columns to see the shape of the data, never the
 *  matrix itself — it runs to hundreds of thousands of rows and several gigabytes. */
export interface MatrixPreview {
  columns: string[];       // sample ids, in order
  rows: Array<{ probe: string; values: (number | null)[] }>;
  total_probes: number;
  total_samples: number;
}

/** The whole matrix as a picture: every probe binned down to `rows` bands, one column per
 *  sample, each value quantised to a byte.
 *
 *  It cannot be the real numbers — 425,930 x 736 is 313 million values, 1.25 GB as float32,
 *  and a <canvas> tops out at 65,535px a side anyway. Binned to 900 rows it is 662 KB, which
 *  is a picture the browser can paint and colour to the app's own palette. */
export interface MatrixOverview {
  rows: number;
  cols: number;
  /** `rows * cols` bytes, row-major, 0-255 mapped over `min`..`max`. */
  values: Uint8Array;
  min: number;
  max: number;
  /** The probe range these bands cover, so a zoom can ask for a narrower one. */
  probe_from: number;
  probe_to: number;
  /** One id per band, when a band is narrow enough to name. Absent on a wide view. */
  probe_ids?: string[];
}

/** What a zoom asks for: a slice of probes and of samples, binned again to fit. */
export interface MatrixOverviewQuery {
  probe_from?: number;
  probe_to?: number;
  sample_from?: number;
  sample_to?: number;
  rows?: number;
}

// ── Stage 4: downstream analysis & literature ────────────────────────────────

/** One comparison the run made. `id` is the pipeline's own name for it, the one its files
 *  carry (`volcano_ad_vs_nci.json`); `label` is what a reader is shown, built from the
 *  `value_labels` the user set back in Strategy & EDA. The two are not interchangeable: the
 *  first is for citing an artifact, the second is for knowing what was compared. */
export interface AnalysisContrast {
  id: string;
  label: string;
  n_significant: number;
  /** Genomic inflation before and after the batch adjustment; null when not computed. */
  lambda_before: number | null;
  lambda_after: number | null;
  /** _interpret_lambda's four-value vocabulary, verbatim. */
  verdict: 'ok' | 'inflated' | 'deflated' | 'not_computed';
  /** Both arms, smaller first. Drives whether the pipeline computed anything at all. */
  n: [number, number] | null;
}

/** What the checks flagged, and how every contrast calibrated. This is the run's own
 *  account of how the analysis was done — it is not a result, which is why the screen keeps
 *  it folded above the findings rather than in front of them. */
export interface AnalysisOverview {
  probes_tested: number;
  findings: OmicsFinding[];
  contrasts: AnalysisContrast[];
}

/** A gene the literature agent researched. Every field is a column of
 *  `literature_review_{contrast}.csv`; nothing here is derived in the browser. */
export interface LiteratureGene {
  gene: string;
  probe: string;
  /** Methylation_Pattern — three values, from a +-0.02 threshold on the delta beta:
   *  Hypermethylated, Hypomethylated, No Change. */
  pattern: string;
  delta_beta: number;
  cohens_d: number;
  n_probes: number;
  /** Novelty_Assessment, normalised by the pipeline to a closed set:
   *  Established, Emerging, Reported, Unknown, Novel. */
  novelty: string;
  function: string;
  pathways: string;
  summary: string;
  /** Key_Sources — up to three URLs, ";"-separated. Links, not prose. */
  sources: string;
}

/** A differentially methylated region, from `dmr_results_{contrast}.csv`. */
export interface DmrRegion {
  chr: string;
  start: number;
  end: number;
  cpgs: number;
  genes: string;
  mean_diff: number;
}

/** An enriched term, from `go_enrichment_` / `kegg_enrichment_{contrast}.csv`. */
export interface PathwayTerm {
  term: string;
  name: string;
  genes: number;
  fdr: number;
}

/** Everything about one contrast.
 *
 *  The three `skipped_*` fields carry the pipeline's own reason string when a section was
 *  not computed — underpowered, or too few significant probes for enrichment. They are what
 *  separates "we looked and found nothing" from "we never looked", which are different
 *  answers and must not render the same. */
export interface ContrastFindings {
  genes: LiteratureGene[];
  dmr: DmrRegion[];
  pathways: PathwayTerm[];
  skipped_genes?: string;
  skipped_dmr?: string;
  skipped_pathways?: string;
  /** Chart ids to fetch for this contrast, e.g. `volcano_ad_vs_nci`. */
  volcano_id: string;
  pval_id: string;
}

@Injectable()
export abstract class OmicsService {
  /** Set once the run is created; every later stage endpoint needs it. */
  sessionId = '';
  readonly sessionName = signal('');

  setSession(id: string): void { this.sessionId = id; }
  abstract setRunName(name: string): void;

  // ── Upload / file browser ─────────────────────────────────
  /** GET /omics/upload/files — every file under the server's root, flat.
   *  The folder tree is derived client-side by splitting `name` on `/`. */
  abstract listFiles(): Observable<UploadFileInfo[]>;

  // ── Stage 1: ingestion ────────────────────────────────────
  /** Creates the run and launches job 1, then streams its progress. Per the
   *  omics API, `ingestion/start` drives BOTH the ingestion and strategy
   *  stages in one job and stops at the review gate, so this single stream
   *  covers everything up to the point a human is needed. */
  abstract startIngestion(config: OmicsRunConfig): Observable<StreamStatus>;
  abstract reconnectIngestion(config: OmicsRunConfig): Observable<StreamStatus>;

  /** GET .../ingestion/summary */
  abstract getIngestionSummary(): Observable<IngestionSummary>;

  // ── Run history ───────────────────────────────────────────
  /** GET /omics/sessions — this pipeline's own runs, most recently updated
   *  first. Separate from tabular's history because the two APIs keep separate
   *  session stores; a tabular run never appears here and vice versa. */
  // ── Stage 2 — Strategy & EDA ────────────────────────────
  // Job 1 already drives this stage (ingestion/start runs harmonization, curation, EDA
  // and the Strategist in one pass), so `start` is an idempotent acknowledgement and the
  // stream is what matters: it ends at the review gate, not at a finished stage.
  abstract startStrategy(): Observable<StreamStatus>;
  abstract reconnectStrategy(): Observable<StreamStatus>;

  /** GET .../strategy/review — everything the review gate shows. */
  abstract getStrategyReview(): Observable<StrategyReview>;

  /** POST .../strategy/charts — re-render the EDA panels for a new selection.
   *  The chart layer reads result files and writes JSON; it touches neither R,
   *  the idat data nor the LLM, so this is seconds and can be re-run freely. */
  abstract rebuildCharts(variables: OmicsEdaVariable[]): Observable<OmicsChart[]>;

  // ── Stage 3 — Processing & Validation ───────────────────
  /** Confirms the gate and launches the heavy job; the stream is what the
   *  loader between the two steps shows. */
  abstract startProcessing(review: StrategyConfirm): Observable<StreamStatus>;
  abstract reconnectProcessing(): Observable<StreamStatus>;

  /** GET .../processing/summary — the numbers the screen is judged by, plus the findings.
   *  Separate from the preview because one is read at a glance and the other is a sample. */
  abstract getProcessingSummary(): Observable<ProcessingSummary>;

  /** GET .../processing/preview — a corner of the matrix, capped on both axes. */
  abstract getMatrixPreview(): Observable<MatrixPreview>;

  /** GET .../processing/overview — the whole matrix binned into an image the browser can
   *  paint. A zoom is the same call with a narrower range, not a scale applied here: the
   *  bands are already averages, and enlarging them shows nothing that was not sent. */
  abstract getMatrixOverview(q?: MatrixOverviewQuery): Observable<MatrixOverview>;

  // ── Stage 4 — Downstream Analysis & Literature ──────────
  /** Idempotent acknowledgement, then the stream: the analyzer cycles through batch
   *  adjustment and the literature review before it finishes, so what the loader shows is
   *  a sequence of stages, not a single wait. */
  abstract startAnalysis(): Observable<StreamStatus>;
  abstract reconnectAnalysis(): Observable<StreamStatus>;

  /** The run's account of itself: what the checks flagged, and lambda per contrast.
   *  GET analysis/results for the warnings, qc_genomic_inflation.csv for the table. */
  abstract getAnalysisOverview(): Observable<AnalysisOverview>;

  /** One contrast's results. Fetched per contrast rather than all at once: a run with
   *  seven of them would otherwise hand over seven literature reviews to show one. */
  abstract getContrastFindings(contrastId: string): Observable<ContrastFindings>;

  /** One chart payload, by the id the contrast named. Same shape the EDA charts use, so
   *  it goes to Chart.js the same way — untouched. */
  abstract getAnalysisChart(chartId: string): Observable<OmicsChart>;

  abstract getSessions(): Observable<OmicsSession[]>;
}
